import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const MaterialGroup = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('material_groups');
  const [viewItem, setViewItem] = useState(null);
  const [materialGroups, setMaterialGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  useEffect(() => {
    loadMaterialGroups();
  }, []);

  const loadMaterialGroups = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/material-groups`);
      setMaterialGroups(response.data || []);
    } catch (error) {
      console.error('Error loading material groups:', error);
      toast.error('Failed to load material groups');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (group = null) => {
    setEditingGroup(group);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      material_group_id: (formData.get('material_group_id') || '').toUpperCase(),
      description: formData.get('description')
    };

    // Additional client-side validation
    if (!editingGroup) {
      const alphanumericRegex = /^[A-Za-z0-9]+$/;
      if (!alphanumericRegex.test(data.material_group_id)) {
        toast.error('Material Group ID must contain only letters and numbers (no spaces or special characters)');
        return;
      }
      
      // Must contain at least one letter
      if (!/[A-Za-z]/.test(data.material_group_id)) {
        toast.error('Material Group ID must contain at least one letter');
        return;
      }
      
      // Must contain at least one number
      if (!/[0-9]/.test(data.material_group_id)) {
        toast.error('Material Group ID must contain at least one number');
        return;
      }
      
      if (data.material_group_id.length > 9) {
        toast.error('Material Group ID cannot exceed 9 characters');
        return;
      }
    }

    try {
      if (editingGroup) {
        await axios.put(`${BACKEND_URL}/api/material-groups/${editingGroup.material_group_id}`, {
          description: data.description
        });
        toast.success('Material Group updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/material-groups`, data);
        toast.success('Material Group created successfully');
      }
      setIsDialogOpen(false);
      loadMaterialGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save material group');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Material Groups</h1>
            <p className="text-sm text-gray-500 mt-1">Manage material group classifications</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Material Groups</h2>
            {writable && (<Button onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Material Group
            </Button>)}
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              <p className="mt-2 text-gray-500">Loading...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Material Group ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Description</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {materialGroups.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center py-8 text-gray-500">
                        No material groups found. Click &quot;Add Material Group&quot; to create one.
                      </td>
                    </tr>
                  ) : (
                    materialGroups.map((group) => (
                      <tr key={group.material_group_id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono text-sm font-semibold">{group.material_group_id}</td>
                        <td className="py-3 px-4 text-sm">{group.description}</td>
                        <td className="py-3 px-4 text-right">
                          <RecordActions doctype="material_groups" onView={() => setViewItem(group)} onEdit={() => handleOpenDialog(group)} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingGroup ? 'Edit Material Group' : 'Add Material Group'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="material_group_id">Material Group ID *</Label>
                  <div className="mt-1">
                    <Input
                      name="material_group_id"
                      defaultValue={editingGroup?.material_group_id || ''}
                      placeholder="e.g., MAT001 or GRP123"
                      maxLength={9}
                      pattern="[A-Za-z0-9]{1,9}"
                      title="Must be alphanumeric (letters and numbers only, no spaces or special characters)"
                      disabled={!!editingGroup}
                      className="uppercase"
                      required
                      style={{ textTransform: 'uppercase' }}
                      onInput={(e) => {
                        // Remove any non-alphanumeric characters and limit to 9 chars
                        const cleaned = e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 9);
                        e.target.value = cleaned.toUpperCase();
                        
                        // Visual feedback for invalid input
                        if (e.target.value !== cleaned.toUpperCase()) {
                          e.target.setCustomValidity('Only letters and numbers allowed');
                        } else {
                          e.target.setCustomValidity('');
                        }
                      }}
                    />
                    <div className="mt-1 space-y-1">
                      <p className="text-xs font-medium text-amber-700">
                        Must contain BOTH letters AND numbers:
                      </p>
                      <p className="text-xs text-gray-600 ml-2">
                        ✓ At least one letter (A-Z)
                      </p>
                      <p className="text-xs text-gray-600 ml-2">
                        ✓ At least one number (0-9)
                      </p>
                      <p className="text-xs text-gray-600 ml-2">
                        ✓ Maximum 9 characters
                      </p>
                      <p className="text-xs text-gray-600 ml-2">
                        ✓ No spaces or special characters
                      </p>
                      <p className="text-xs text-green-600 font-medium mt-1">
                        Examples: MAT001, GRP123, ABC456
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <Label>Description *</Label>
                  <Input
                    name="description"
                    defaultValue={editingGroup?.description || ''}
                    placeholder="e.g., Raw Materials"
                    required
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingGroup ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default MaterialGroup;
